import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, apiFetch, getToken } from '../lib/api';
import { Btn, PERSON_COLORS, fmt12 } from './ui';
import { usePeople } from '../context/PeopleContext';
import { localizeEventTime } from '../lib/tz';
import EmailSearch from './EmailSearch';
import useIsMobile from '../hooks/useIsMobile';
import SheetCheckPanel from './SheetCheckPanel';
import PersonBioCard from './PersonBioCard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Match the email template's label regex so the inline notes preview here
// matches what recipients will actually see (LABEL: value rows + *italic).
const LABEL_RE = /^([A-Za-z][^:]{0,40}):\s*(.*)$/;

function parsePreviewRows(notes) {
  if (!notes) return [];
  return notes.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
    if (line.startsWith('*')) return { kind: 'italic', text: line };
    const m = line.match(LABEL_RE);
    return m ? { kind: 'label', label: m[1].toUpperCase(), value: m[2] } : { kind: 'plain', text: line };
  });
}

// Compact per-event editor that focuses on what admins actually tweak in
// the last-mile review window — title, time, and (most importantly) the
// notes field with the same DRIVER/GREETER/PAX label chips and live row
// preview as the SheetBuilder editor. Saves on blur via onSave({...patch}).
function EventInlineEditor({ event, personColor, onSave }) {
  const [title, setTitle] = useState(event.title || '');
  const [timeStart, setTimeStart] = useState(event.timeStart || '');
  const [notes, setNotes] = useState(event.notes || '');
  const notesRef = useRef(null);

  // Reset state when the event prop changes (e.g., after a refresh that
  // returns canonical server values).
  // eslint-disable-next-line
  useEffect(() => {
    setTitle(event.title || '');
    setTimeStart(event.timeStart || '');
    setNotes(event.notes || '');
  }, [event.id]);

  const insertNoteLabel = (label) => {
    setNotes(prev => {
      const trimmed = (prev || '').replace(/\s+$/, '');
      const sep = trimmed ? '\n' : '';
      return label === '*' ? `${trimmed}${sep}*` : `${trimmed}${sep}${label}: `;
    });
    setTimeout(() => {
      const el = notesRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }, 0);
  };

  const flush = (patch) => {
    // Only fire onSave if the patched value actually differs from the saved
    // event — saves a network round-trip when the admin tabs through fields
    // without changing anything.
    const changed = Object.entries(patch).some(([k, v]) => (event[k] || '') !== (v || ''));
    if (changed) onSave(patch);
  };

  const inp = { background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'7px 10px', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };
  const previewRows = parsePreviewRows(notes);

  return (
    <div style={{ background:'var(--bg)', borderRadius:10, padding:12, marginTop:8, border:'1px solid var(--border)' }}>
      <div style={{ display:'flex', gap:8, marginBottom:8 }}>
        <input value={timeStart} onChange={e=>setTimeStart(e.target.value)} onBlur={() => flush({ timeStart: timeStart || null })}
          placeholder="HH:MM" style={{ ...inp, width:80, flex:'none', textAlign:'center', fontVariantNumeric:'tabular-nums' }} />
        <input value={title} onChange={e=>setTitle(e.target.value)} onBlur={() => flush({ title })}
          placeholder="Event title" style={{ ...inp, fontWeight:700 }} />
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:6 }}>
        {['DRIVER','GREETER','PAX','NOTE'].map(l => (
          <button key={l} type="button" onClick={() => insertNoteLabel(l)}
            style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:999, padding:'3px 9px', fontSize:10, fontWeight:700, letterSpacing:0.4, color:'var(--text2)', cursor:'pointer', fontFamily:'inherit' }}>
            + {l}
          </button>
        ))}
        <button type="button" onClick={() => insertNoteLabel('*')}
          style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:999, padding:'3px 9px', fontSize:10, fontWeight:700, color:'var(--text3)', fontStyle:'italic', cursor:'pointer', fontFamily:'inherit' }}>
          + *italic
        </button>
      </div>

      <textarea ref={notesRef} value={notes} onChange={e=>setNotes(e.target.value)} onBlur={() => flush({ notes: notes || null })}
        placeholder={"DRIVER: Mike 555-1234\nGREETER: Holly 555-5678\nPAX: K2 (1A), JJ (2D)"}
        rows={Math.max(3, notes.split('\n').length)} style={{ ...inp, resize:'vertical', lineHeight:1.5 }} />

      {/* Pillar 1: auto-bio when the title references a person. The
          card itself decides whether to render and is opt-in for the
          actual bio fetch — we just plumb the latest title + notes. */}
      <PersonBioCard title={title} notes={notes} />

      {previewRows.length > 0 && (
        <div style={{ marginTop:10, background:'#ffffff', border:'1px solid var(--border)', borderRadius:8, padding:'10px 12px', position:'relative' }}>
          <div style={{ position:'absolute', top:-7, left:10, background:'var(--bg)', color:'var(--text3)', fontSize:8, fontWeight:800, letterSpacing:1.4, padding:'0 6px' }}>EMAIL ROW</div>
          {previewRows.map((r, i) => {
            const mt = i === 0 ? 0 : 3;
            if (r.kind === 'italic') return <div key={i} style={{ fontSize:11, color:'#888', fontStyle:'italic', marginTop:mt }}>{r.text}</div>;
            if (r.kind === 'label')  return <div key={i} style={{ fontSize:12, color:'#333', marginTop:mt }}><strong style={{ color: personColor }}>{r.label}:</strong> {r.value}</div>;
            return <div key={i} style={{ fontSize:12, color:'#555', marginTop:mt }}>{r.text}</div>;
          })}
        </div>
      )}
    </div>
  );
}

export default function ReviewAndSendModal({ sheet: initialSheet, onClose, onSent }) {
  const isMobile = useIsMobile();
  const { colorMap } = usePeople();
  const [sheet, setSheet] = useState(initialSheet);
  const [events, setEvents] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  // Bumped whenever the schedule or sheet notes change so SheetCheckPanel
  // re-runs the rule engine. Cheap (server endpoint is pure-code) so we
  // can refresh aggressively without throttling.
  const [checkRefreshKey, setCheckRefreshKey] = useState(0);

  // Recipients (mirrored from old SendModal — kept identical so the send
  // payload matches what the API already accepts).
  const contacts = sheet.artist?.distributionContacts || [];
  const groups = sheet.artist?.distributionGroups || [];
  const fallbackList = sheet.artist?.distributionList || [];
  const [selectedGroups, setSelectedGroups] = useState(() => new Set());
  const [excludedContacts, setExcludedContacts] = useState(() => new Set());
  const [extraEmails, setExtraEmails] = useState([]);
  const [oneOff, setOneOff] = useState('');
  const [sending, setSending] = useState(false);
  // Publish-on-send: when the sheet is still a draft (typical SheetBuilder
  // flow), the modal also publishes + fires push to viewers as part of the
  // single "Send" click — matching the old "Publish & Send Email" button's
  // contract so admins don't end up with a sent-but-unpublished sheet.
  const isDraft = (sheet.status || 'draft') === 'draft';
  const [sendPush, setSendPush] = useState(true);

  const refreshPreview = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/email/preview/${sheet.id}`, {
        headers: { Authorization: `Bearer ${getToken() || ''}` },
      });
      const html = await res.text();
      setPreviewHtml(html);
    } catch (e) { console.error('preview fetch failed', e); }
    finally { setPreviewLoading(false); }
  }, [sheet.id]);

  useEffect(() => { refreshPreview(); }, [refreshPreview]);
  useEffect(() => {
    api.getEvents(sheet.id).then(({ events }) => setEvents(events || []))
      .catch(e => console.error('events load failed', e));
  }, [sheet.id]);

  // ── Sheet notes (debounced auto-save) ──────────────────────────────────────
  const notesTimer = useRef(null);
  const handleNotesChange = (val) => {
    setSheet(prev => ({ ...prev, notes: val }));
    clearTimeout(notesTimer.current);
    setSavingNotes(true);
    notesTimer.current = setTimeout(async () => {
      try {
        await api.updateSheet(sheet.id, { notes: val || null });
        await refreshPreview();
        setCheckRefreshKey(k => k + 1);
      }
      catch (e) { console.error('save sheet notes failed', e); }
      finally { setSavingNotes(false); }
    }, 600);
  };

  // ── Event save (immediate on blur from inline editor) ──────────────────────
  const saveEvent = async (eventId, patch) => {
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, ...patch } : e));
    try {
      await api.updateEvent(eventId, patch);
      await refreshPreview();
      setCheckRefreshKey(k => k + 1);
    }
    catch (e) { console.error('save event failed', e); }
  };

  // ── Recipient logic (lifted directly from SendModal) ───────────────────────
  const getRecipients = () => {
    const emailSet = new Set();
    contacts.forEach(c => {
      if (excludedContacts.has(c.id)) return;
      const inGroup = (c.groupIds || []).some(gid => selectedGroups.has(gid));
      const isUngrouped = !c.groupIds || c.groupIds.length === 0;
      if (inGroup || (isUngrouped && selectedGroups.has('ungrouped'))) emailSet.add(c.email);
    });
    extraEmails.forEach(e => emailSet.add(e));
    return [...emailSet];
  };
  const recipientEmails = getRecipients();
  const toggleGroup = (gid) => setSelectedGroups(prev => { const n = new Set(prev); if (n.has(gid)) n.delete(gid); else n.add(gid); return n; });
  const addOneOff = () => {
    const email = oneOff.trim().toLowerCase();
    if (!email.includes('@')) return;
    if (!extraEmails.includes(email) && !recipientEmails.includes(email)) setExtraEmails(prev => [...prev, email]);
    setOneOff('');
  };
  const handleSend = async () => {
    setSending(true);
    try {
      const groupIds = contacts.length > 0 ? [...selectedGroups] : [];
      // Draft → publish first so the sheet flips to published + push fires
      // to viewers. Mirrors the payload of the old inline "Publish & Send"
      // path so existing recipients/push logic on the server is unchanged.
      if (isDraft) {
        await apiFetch(`/sheets/${sheet.id}/publish`, {
          method: 'POST',
          body: JSON.stringify({ emailGroupIds: groupIds, emailExtraRecipients: extraEmails, sendPush }),
        });
      }
      await api.sendEmail(sheet.id, groupIds, extraEmails);
      onSent?.(recipientEmails.length);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSending(false); }
  };

  // Sort events for display same way as the email. Hotel-typed events are
  // hidden — they're redundant with the "Hotel:" printout block (see the
  // email template's matching filter).
  const sortedEvents = [...events]
    .filter(e => e.travelType !== 'hotel')
    .sort((a, b) => {
      if (a.nextDay && !b.nextDay) return 1;
      if (!a.nextDay && b.nextDay) return -1;
      return (a.timeStart || 'zz').localeCompare(b.timeStart || 'zz');
    });

  const dateStr = new Date(sheet.date + 'T12:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' });
  const inp = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'9px 12px', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };

  // ── Layout ─────────────────────────────────────────────────────────────────
  // Desktop: two columns (preview | edit+send). Mobile: stacked with preview
  // on top in a fixed-height window so the editor stays reachable.
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.78)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding: isMobile ? 0 : 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--card)', borderRadius: isMobile ? 0 : 16, width:'100%', height:'100%', maxWidth: 1280, maxHeight: isMobile ? '100%' : '94vh', display:'flex', flexDirection: isMobile ? 'column' : 'row', overflow:'hidden', border:'1px solid var(--border)' }}>

        {/* ── Left: live email preview ─────────────────────────────────────── */}
        <div style={{ flex: isMobile ? 'none' : 1, height: isMobile ? '40vh' : 'auto', borderRight: isMobile ? 'none' : '1px solid var(--border)', borderBottom: isMobile ? '1px solid var(--border)' : 'none', background:'#f1f5f9', display:'flex', flexDirection:'column', minWidth:0 }}>
          <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)', background:'var(--card)', fontSize:11, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', textTransform:'uppercase', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span><i className="fa-solid fa-eye" style={{ marginRight:6 }}></i>Email preview</span>
            {previewLoading && <span style={{ color:'var(--text3)', fontWeight:500, fontSize:10, fontStyle:'italic' }}>updating…</span>}
          </div>
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            style={{ flex:1, border:'none', background:'#fff', width:'100%' }}
            sandbox="allow-same-origin"
          />
        </div>

        {/* ── Right: editor + recipients + send ─────────────────────────────── */}
        <div style={{ width: isMobile ? '100%' : 460, flexShrink:0, display:'flex', flexDirection:'column', minHeight:0 }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <h3 style={{ fontSize:16, fontWeight:800, color:'var(--text)', margin:0 }}>
                <i className="fa-solid fa-paper-plane" style={{ marginRight:8, color:'var(--blue)' }}></i>
                Review & Send
              </h3>
              <p style={{ fontSize:12, color:'var(--text2)', margin:'3px 0 0' }}>
                {sheet.artist?.name} &middot; {dateStr}
              </p>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:16, cursor:'pointer', color:'var(--text3)', padding:8 }}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>

            {/* Sheet-level note (yellow card in email) */}
            <div style={{ marginBottom:18 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
                <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', margin:0, textTransform:'uppercase' }}>
                  <i className="fa-solid fa-note-sticky" style={{ marginRight:5, color:'#f59e0b' }}></i>Sheet Note
                </p>
                {savingNotes && <span style={{ fontSize:10, color:'var(--text3)', fontStyle:'italic' }}>saving…</span>}
              </div>
              <textarea value={sheet.notes || ''} onChange={e => handleNotesChange(e.target.value)}
                placeholder="Top-of-sheet note (renders as yellow card). Use **bold** for emphasis."
                rows={3} style={{ ...inp, resize:'vertical', lineHeight:1.5 }} />
            </div>

            {/* Sheet check — Pillar 2 MVP. Surfaces deterministic findings
                (soundcheck order, missing soundcheck on show day, tight
                airport buffers, suspicious midnight rows) before send. */}
            <SheetCheckPanel
              sheetId={sheet.id}
              refreshKey={checkRefreshKey}
              onFocusEvent={(eventId) => setExpandedEventId(eventId)}
            />

            {/* Schedule */}
            <div style={{ marginBottom:18 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', margin:0, textTransform:'uppercase' }}>
                  <i className="fa-solid fa-list-ul" style={{ marginRight:5 }}></i>Schedule
                </p>
                <a href={`/sheet/${sheet.id}/build`} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize:11, color:'var(--blue)', textDecoration:'none', fontWeight:600 }}>
                  Open full editor →
                </a>
              </div>
              {sortedEvents.length === 0 ? (
                <p style={{ fontSize:12, color:'var(--text3)', fontStyle:'italic' }}>No events on this sheet yet.</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {sortedEvents.map(e => {
                    const expanded = expandedEventId === e.id;
                    const firstPerson = (e.person || '').split(',')[0]?.trim();
                    const personColor = colorMap[firstPerson] || PERSON_COLORS[firstPerson] || '#94a3b8';
                    const noteCount = (e.notes || '').split('\n').filter(Boolean).length;
                    const { localTime, sourceLabel } = localizeEventTime(e, sheet.timezone, fmt12);
                    return (
                      <div key={e.id} style={{ border:'1px solid var(--border)', borderLeft:`3px solid ${personColor}`, borderRadius:8, overflow:'hidden' }}>
                        <button onClick={() => setExpandedEventId(expanded ? null : e.id)}
                          style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'9px 12px', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                          <span style={{ display:'flex', flexDirection:'column', minWidth:64, gap:2 }}>
                            <span style={{ fontSize:12, fontWeight:700, color:'var(--text2)', fontVariantNumeric:'tabular-nums' }}>
                              {localTime || '—'}
                            </span>
                            {sourceLabel && (
                              <span style={{ fontSize:9, fontWeight:800, color:'#b45309', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:999, padding:'1px 5px', whiteSpace:'nowrap', alignSelf:'flex-start' }}>
                                {sourceLabel}
                              </span>
                            )}
                          </span>
                          <span style={{ flex:1, fontSize:13, fontWeight:600, color:'var(--text)' }}>{e.title || '(untitled)'}</span>
                          {noteCount > 0 && <span style={{ fontSize:10, color:'var(--text3)' }}>{noteCount} {noteCount === 1 ? 'note' : 'notes'}</span>}
                          <i className={`fa-solid fa-chevron-${expanded ? 'up' : 'down'}`} style={{ fontSize:10, color:'var(--text3)' }}></i>
                        </button>
                        {expanded && (
                          <div style={{ padding:'0 12px 12px' }}>
                            <EventInlineEditor event={e} personColor={personColor}
                              onSave={(patch) => saveEvent(e.id, patch)} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recipients (compact) */}
            <div style={{ marginBottom:14 }}>
              <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', marginBottom:8, textTransform:'uppercase' }}>
                <i className="fa-solid fa-paper-plane" style={{ marginRight:5 }}></i>Recipients
              </p>

              {groups.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:8 }}>
                  {groups.map(g => {
                    const members = contacts.filter(c => (c.groupIds || []).includes(g.id));
                    const checked = selectedGroups.has(g.id);
                    return (
                      <button key={g.id} onClick={() => toggleGroup(g.id)}
                        style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 11px', borderRadius:18, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:700, border:'1.5px solid', transition:'all 0.12s',
                          background: checked ? 'rgba(30,64,175,0.1)' : 'transparent',
                          borderColor: checked ? 'var(--blue)' : 'var(--border)',
                          color: checked ? 'var(--blue)' : 'var(--text3)' }}>
                        {checked && <i className="fa-solid fa-check" style={{ fontSize:8 }}></i>}
                        {g.name} <span style={{ opacity:0.6 }}>({members.length})</span>
                      </button>
                    );
                  })}
                  {(() => {
                    const ungrouped = contacts.filter(c => !c.groupIds || c.groupIds.length === 0);
                    if (!ungrouped.length) return null;
                    const checked = selectedGroups.has('ungrouped');
                    return (
                      <button onClick={() => toggleGroup('ungrouped')}
                        style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 11px', borderRadius:18, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:700, border:'1.5px solid',
                          background: checked ? 'rgba(30,64,175,0.1)' : 'transparent',
                          borderColor: checked ? 'var(--blue)' : 'var(--border)',
                          color: checked ? 'var(--blue)' : 'var(--text3)' }}>
                        {checked && <i className="fa-solid fa-check" style={{ fontSize:8 }}></i>}
                        Ungrouped <span style={{ opacity:0.6 }}>({ungrouped.length})</span>
                      </button>
                    );
                  })()}
                </div>
              )}

              {contacts.length === 0 && fallbackList.length > 0 && (
                <p style={{ fontSize:11, color:'var(--text2)', margin:'0 0 8px', lineHeight:1.5 }}>{fallbackList.join(', ')}</p>
              )}

              <div style={{ display:'flex', gap:6 }}>
                <div style={{ flex:1 }}>
                  <EmailSearch value={oneOff} onChange={setOneOff}
                    onSelect={(email) => { if (!recipientEmails.includes(email)) setExtraEmails(prev => [...prev, email]); }}
                    placeholder="Add one-off email…" style={{ ...inp, fontSize:12, padding:'7px 10px' }} />
                </div>
                <button onClick={addOneOff} disabled={!oneOff.trim().includes('@')}
                  style={{ padding:'7px 12px', borderRadius:8, background:'var(--blue)', border:'none', color:'white', cursor:'pointer', fontSize:11, fontWeight:700, opacity: oneOff.trim().includes('@') ? 1 : 0.4 }}>
                  Add
                </button>
              </div>
              {extraEmails.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                  {extraEmails.map(e => (
                    <span key={e} style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:10, fontWeight:600, color:'var(--teal, #06B6D4)', background:'rgba(6,182,212,0.06)', border:'1px solid rgba(6,182,212,0.15)', borderRadius:14, padding:'3px 8px' }}>
                      {e}
                      <button onClick={() => setExtraEmails(prev => prev.filter(x => x !== e))} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(6,182,212,0.5)', fontSize:9, padding:0 }}>
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer / send */}
          <div style={{ borderTop:'1px solid var(--border)', background:'var(--card)' }}>
            {isDraft && (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom:'1px solid var(--border)' }}>
                <i className="fa-solid fa-bell" style={{ fontSize:12, color:'#22c55e' }}></i>
                <span style={{ flex:1, fontSize:12, fontWeight:600, color:'var(--text2)' }}>Push notification to viewers</span>
                <button onClick={() => setSendPush(prev => !prev)}
                  style={{ padding:'4px 12px', borderRadius:14, fontSize:11, fontWeight:700, border:'none', cursor:'pointer', fontFamily:'inherit',
                    background: sendPush ? '#22c55e' : 'var(--bg)', color: sendPush ? '#fff' : 'var(--text3)' }}>
                  {sendPush ? 'On' : 'Off'}
                </button>
              </div>
            )}
            <div style={{ padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <p style={{ fontSize:12, color:'var(--text2)', margin:0 }}>
                <strong style={{ color:'var(--text)' }}>{recipientEmails.length}</strong> recipient{recipientEmails.length !== 1 ? 's' : ''}
                {sheet.emailSentAt && <span style={{ color:'#fbbf24', marginLeft:8, fontSize:10 }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ marginRight:3 }}></i>sent before
                </span>}
              </p>
              <div style={{ display:'flex', gap:8 }}>
                <Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn>
                <Btn variant="success" size="sm" onClick={handleSend} disabled={sending || recipientEmails.length === 0}>
                  {sending ? 'Sending…' : isDraft ? `Publish & Send to ${recipientEmails.length}` : `Send to ${recipientEmails.length}`}
                </Btn>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
